import { Component, Input, Output, EventEmitter } from '@angular/core';

/**
 * BasicTextboxComponent
 *
 * Purpose
 * - PCC-styled text input field with consistent design tokens
 * - Supports labels, error states, disabled state, and optional clear button
 * - Two-way data binding for form integration
 * - Customizable width for flexible layouts
 *
 * Visual states
 * - default: standard input field with border
 * - hover: subtle border highlight
 * - focus: blue border (#0671E0) with glow effect
 * - error: red border with error message display
 * - disabled: grayed out appearance, no interaction
 *
 * Features
 * - Optional label above the input
 * - Optional error text with icon below the input
 * - Optional clear button (X icon) that appears when input has value
 * - Customizable width (default: 350px)
 *
 * Inputs
 * - placeholder: string (optional) - Placeholder text shown when input is empty
 * - model: string (default: '') - Two-way bindable input value
 * - width: string (default: '350px') - Custom width for the input field (e.g., '100%', '200px')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - readonly: boolean (default: false) - Makes the input readonly (prevents editing but allows selection)
 * - label: string (optional) - Label text displayed above the input field
 * - hasError: boolean (default: false) - Applies error styling (red border)
 * - errorText: string (optional) - Error message displayed below the input when hasError is true
 * - showClearButton: boolean (default: false) - Shows a clear (X) button when input has value
 *
 * Outputs
 * - modelChange: string - Emits the new input value on change (supports [(model)] two-way binding)
 * - click: MouseEvent - Emits click events on the input field
 *
 * Usage examples
 *
 * <!-- Basic text input -->
 * <app-basic-textbox placeholder="Enter your name" [(model)]="name"></app-basic-textbox>
 *
 * <!-- With label -->
 * <app-basic-textbox label="Username" placeholder="Enter username" [(model)]="username"></app-basic-textbox>
 *
 * <!-- Two-way binding -->
 * <app-basic-textbox [(model)]="email" label="Email" placeholder="user@example.com"></app-basic-textbox>
 *
 * <!-- With error state -->
 * <app-basic-textbox
 *   [(model)]="phone"
 *   label="Phone Number"
 *   [hasError]="hasValidationError"
 *   errorText="Please enter a valid phone number">
 * </app-basic-textbox>
 *
 * <!-- With clear button -->
 * <app-basic-textbox
 *   [(model)]="searchTerm"
 *   placeholder="Search..."
 *   [showClearButton]="true">
 * </app-basic-textbox>
 *
 * <!-- Custom width -->
 * <app-basic-textbox [(model)]="address" label="Address" width="100%"></app-basic-textbox>
 *
 * <!-- Disabled state -->
 * <app-basic-textbox [model]="readonlyValue" [disabled]="true" label="Read-only field"></app-basic-textbox>
 *
 * <!-- Readonly state -->
 * <app-basic-textbox [model]="readonlyValue" [readonly]="true" label="Read-only field"></app-basic-textbox>
 *
 * <!-- With click event -->
 * <app-basic-textbox [(model)]="searchTerm" (click)="handleClick($event)" placeholder="Click to search"></app-basic-textbox>
 */

@Component({
    selector: 'app-basic-textbox',
    templateUrl: './basic-textbox.component.html',
    styleUrls: ['./basic-textbox.component.scss'],
    standalone: false
})
export class BasicTextboxComponent {
  @Input() placeholder: string = '';
  @Input() model: string = '';
  @Output() modelChange = new EventEmitter<string>();
  @Input() width: string = '350px';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() label: string = '';
  @Input() hasError: boolean = false;
  @Input() errorText: string = '';
  @Input() showClearButton: boolean = false;
  @Input() size: 'x-small' | 'small' | 'medium' | 'large' = 'medium';
  @Output() click = new EventEmitter<MouseEvent>();

  get hasValue(): boolean {
    return !!this.model && this.model.length > 0;
  }

  onInput(value: string): void {
    this.model = value;
    this.modelChange.emit(this.model);
  }

  onClear(): void {
    if (!this.disabled) {
      this.model = '';
      this.modelChange.emit('');
    }
  }

  onClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.click.emit(event);
    }
  }
}

